import {
  ColumnApiModule,
  EventApiModule,
  ModuleRegistry,
  NumberEditorModule,
  PaginationModule,
  RowApiModule,
  RowSelectionModule,
  ValidationModule,
} from "ag-grid-community";
import {
  CellSelectionModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ContextMenuModule,
  CustomEditorModule,
  ExternalFilterModule,
  GroupFilterModule,
  HighlightChangesModule,
  LicenseManager,
  MasterDetailModule,
  QuickFilterModule,
  RichSelectModule,
  RowAutoHeightModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  RowStyleModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  TextEditorModule,
  TooltipModule,
  UndoRedoEditModule,
} from "ag-grid-enterprise";
import mixpanel from "mixpanel-browser";
import { createRoot } from "react-dom/client";

import * as chmln from "@chamaeleonidae/chmln";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import * as Sentry from "@sentry/react";

import { message } from "./utils/message";
import { widgetAuth } from "./utils/widgetAuth";
import { App } from "./App";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-076655}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Intelligems}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{intelligems.io}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{intelligems.io}_need_to_be_licensed___{intelligems.io}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{6_February_2026}____[v3]_[01]_MTc3MDMzNjAwMDAwMA==4a993a3369ba60e3c8f6ee1924f099f7"
);

const prodHost = "app.intelligems.io";
const devHost = "app-dev.intelligems.io";
const devToken = "eacc2d71bd5d818fed4768c641aeb284";
const prodToken = "aaf6d392bdbfdc48717cb30ff41f15b7";
const isProd = window.location.hostname === prodHost;
const isDev = window.location.hostname === devHost;

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  CellSelectionModule,
  ContextMenuModule,
  ColumnMenuModule,
  GroupFilterModule,
  RowGroupingPanelModule,
  RowSelectionModule,
  RowApiModule,
  CellStyleModule,
  RowStyleModule,
  TooltipModule,
  ClientSideRowModelApiModule,
  UndoRedoEditModule,
  HighlightChangesModule,
  CustomEditorModule,
  ColumnAutoSizeModule,
  NumberEditorModule,
  TextEditorModule,
  RichSelectModule,
  QuickFilterModule,
  MasterDetailModule,
  PaginationModule,
  ClipboardModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  EventApiModule,
  ColumnApiModule,
  ExternalFilterModule,
  RowAutoHeightModule,
  ...(!isProd ? [ValidationModule] : []),
]);

if (isProd) {
  mixpanel.init(prodToken);
} else {
  mixpanel.init(devToken, { debug: true });
}

export const REACT_QUERY_CACHE = "REACT_QUERY_OFFLINE_CACHE";

const ignoreErrors = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
  "Failed to read the 'localStorage' property from 'Window': Access is denied for this document.",
  "The operation is insecure.",
  "Failed to read the 'sessionStorage' property from 'Window': Access is denied for this document.",
  "Non-Error promise rejection captured",
  "Cannot read properties of undefined (reading 'content')",
  "Request aborted",
  "Failed to get Hubspot Token",
  "Cannot read properties of null (reading 'style')",
  `The pseudo class ":nth-child" is potentially unsafe`,
  "signal is aborted without reason",
  "Failed to fetch (browser-intake-datadoghq.com)",
];

const dataDogEnabled = () => {
  const stage = import.meta.env.VITE_STAGE || "local";
  // console.log("Datadog stage:", stage, "hostname:", window.location.hostname);

  // Enable for all environments except when on share pages in production
  if (stage === "prod" && window.location.pathname.startsWith("/share")) {
    return false;
  }
  return true;
};

const isDatadogEnabled = dataDogEnabled();

datadogRum.init({
  applicationId: "558c4125-c8d8-4159-ac27-35158e679ce3",
  clientToken: "pub5eebdb5bd67b2e625d29cc1a25d1e386",
  site: "datadoghq.com",
  service: "intelligems-app",
  env: import.meta.env.VITE_STAGE,
  version: import.meta.env.VITE_SENTRY_RELEASE || "local",
  sessionSampleRate: isDatadogEnabled ? 100 : 0,
  sessionReplaySampleRate: isDatadogEnabled ? 100 : 0,
  trackUserInteractions: isDatadogEnabled,
  trackResources: isDatadogEnabled,
  trackLongTasks: isDatadogEnabled,
  traceSampleRate: isDatadogEnabled ? 100 : 0,
  allowedTracingUrls: [/.*\.intelligems.io.*/],
  defaultPrivacyLevel: "allow",
  beforeSend: (event) => {
    if (event.type === "error") {
      if (
        ignoreErrors.some((ignoreError) =>
          event.error.message.includes(ignoreError)
        )
      ) {
        return false;
      }
    }
    return true;
  },
});

// Start Datadog RUM if enabled
if (isDatadogEnabled) {
  datadogRum.startSessionReplayRecording();
  // console.log("Datadog RUM initialized and session replay started");
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // @ts-ignore
  integrations: [],
  release: import.meta.env.VITE_SENTRY_RELEASE,
  environment: import.meta.env.VITE_STAGE,
  tracesSampleRate: 1.0,
  maxBreadcrumbs: 50,
  ignoreErrors, //Add any errors you want to ignore here
  normalizeDepth: 10, // how deep of redux state to push
  enabled: import.meta.env.VITE_RUNNING_LOCAL !== "true",
});

chmln.init(
  "SxlAgsazrkfpVoGLnGoPgckkd8lwl6fW3fkHdtpP87buvK-1TIXQ8-FJs4pClLJBcELMEL",
  { fastUrl: "https://fast.chameleon.io/" }
);

const _root = <App />;

const container = document.getElementById("root");

if (
  window.parent === window ||
  window["IS_MARKUP_TOP_FRAME"] ||
  window.parent["MARKUP_EXTENSION_ID"]
) {
  window.addEventListener(
    "beforeunload",
    () => {
      console.log("beforeunload");
      message({ type: "CLOSED" }, "*");
    },
    { capture: true }
  );
  const root = createRoot(container!);
  root.render(_root);
} else {
  // inside an iframe
  widgetAuth();
}
